import { useEffect, useRef } from "react";

interface DismissContainerRef {
  current: HTMLElement | null;
}

/**
 * Closes an open popup when the user presses outside it, or presses Escape.
 * `containerRef` must wrap the trigger as well as the panel, so pressing the
 * trigger to close doesn't count as being outside.
 */
export function useDismissOnOutsideClick(
  containerRef: DismissContainerRef,
  open: boolean,
  onDismiss: () => void
) {
  // In a ref, so an inline callback doesn't resubscribe every render.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    // mousedown, not click: a click on the trigger would dismiss then reopen.
    const handlePressOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      // A target gone from the page was removed by its own press - a select's clear
      // button - so where it was can't be told, and it is not a press outside.
      if (target && target.isConnected && !containerRef.current?.contains(target)) {
        onDismissRef.current();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismissRef.current();
      }
    };

    document.addEventListener("mousedown", handlePressOutside);
    document.addEventListener("touchstart", handlePressOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePressOutside);
      document.removeEventListener("touchstart", handlePressOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, open]);
}

export default useDismissOnOutsideClick;
